import { useState, useCallback, useRef, useEffect } from 'react'
import { Paging } from 'types/global'
import { PLAYERS_FETCH_SIZE_ANONYMOUS } from 'constants/global'

/**
 *
 * @param fetchFn fetching function
 * @param increment amount to increment by
 */
export const useLoadMoreFetch = (
  fetchFn: (amount: number) => void | Promise<void>,
  increment: number
): [number, boolean, (prevFetched: number, maxLimit?: number) => void, () => void] => {
  const [fetched, setFetched] = useState<number>(increment)
  const [loading, setLoading] = useState<boolean>(false)
  const loadMore = useCallback(
    async (fetched: number, maxLimit?: number) => {
      setLoading(true)
      const newFetch = Math.min(fetched + increment, maxLimit || Infinity)
      try {
        await fetchFn(newFetch)
      } finally {
        setLoading(false)
      }
      setFetched(newFetch)
    },
    [fetchFn, increment]
  )
  return [
    fetched,
    loading,
    loadMore,
    useCallback(() => setFetched(increment), [setFetched, increment]),
  ]
}

interface PaginatedFetchOptions {
  fetch: ((paging: Paging) => Promise<any[]>) | ((paging: Paging) => void)
  clearResults?: () => any
  pageSize?: number
  total?: number
  totalFetched?: number
  invalidateAutoFetch?: boolean
}

export const usePaginatedFetch = (
  {
    fetch,
    pageSize = PLAYERS_FETCH_SIZE_ANONYMOUS,
    total,
    totalFetched,
    clearResults,
    invalidateAutoFetch,
  }: PaginatedFetchOptions,
  deps: any[]
): [() => Promise<void>, boolean, boolean] => {
  const fetched = useRef<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const allFetched = useCallback(
    () => total !== undefined && total > 0 && fetched.current >= total,
    [total]
  )
  const loadPage = useCallback(async () => {
    if (!allFetched()) {
      setLoading(true)
      try {
        await fetch({ skip: fetched.current, limit: pageSize })
        fetched.current += pageSize
      } finally {
        setLoading(false)
      }
    }
  }, [setLoading, allFetched, fetched, pageSize, fetch])

  if (typeof totalFetched === 'number') fetched.current = totalFetched

  const depsChanged = useDetectDepsChange(deps)

  useEffect(() => {
    if (depsChanged && !invalidateAutoFetch) {
      fetched.current = totalFetched || 0
      clearResults && clearResults()
      loadPage && loadPage()
    }
  })
  return [loadPage, loading, allFetched()]
}

const useDetectDepsChange = (deps: any[]) => {
  const saved = useRef<any[]>()
  const prev = saved.current
  saved.current = deps
  return !prev || deps.length !== prev.length || deps.some((d, i) => d !== prev[i])
}
